import React from 'react'
import { Button, Table, Th, Td, Tr, Badge } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreVertical } from 'lucide-react'
import { listMyBankAccounts, deleteMyBankAccount, setDefaultMyBankAccount, type BankAccount } from '@/modules/affiliates/services/affiliateBankService'
import BankAccountEditModal from './BankAccountEditModal'

function labelMethod(m?: string) { return (m === 'ted' ? 'TED' : 'PIX') }

export default function BankAccountsManager() {
  const [rows, setRows] = React.useState<BankAccount[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string|null>(null)
  const [open, setOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<BankAccount | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true); setError(null)
    try { setRows(await listMyBankAccounts()) } catch (e:any) { setError(e?.message || 'Falha ao carregar contas bancárias') } finally { setLoading(false) }
  }, [])
  React.useEffect(()=> { load() }, [load])

  async function onDelete(id: string) { if (!confirm('Excluir este registro?')) return; try { await deleteMyBankAccount(id); await load() } catch (e:any) { setError(e?.message || 'Falha ao excluir') } }
  async function onMakeDefault(id: string) { try { await setDefaultMyBankAccount(id); await load() } catch (e:any) { setError(e?.message || 'Falha ao definir como padrão') } }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-base font-medium">Dados bancários</div>
        <Button onClick={()=> { setEditing(null); setOpen(true) }}>Adicionar dados</Button>
      </div>
      {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
      {loading ? (
        <div className="text-sm text-foreground/70">Carregando...</div>
      ) : (
        <Table>
          <thead>
            <Tr><Th>Método</Th><Th>Titular</Th><Th>Documento</Th><Th>Chave/Conta</Th><Th>Status</Th><Th className="w-40 text-right">Ações</Th></Tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <Tr key={r.id}>
                <Td>{labelMethod(r.method)}</Td>
                <Td>{r.holder_name}</Td>
                <Td>{r.document || '—'}</Td>
                <Td>{r.method==='pix' ? (r.pix_key_value || '—') : `${r.bank_code || ''} ${r.branch || ''} ${r.account_number || ''}`}</Td>
                <Td>{r.is_default ? <Badge variant="soft" color="success">Padrão</Badge> : <span className="text-foreground/70">—</span>}</Td>
                <Td className="text-right">
                  <DropdownMenu
                    align="end"
                    trigger={<Button variant="outline" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button>}
                    items={[
                      { label: 'Editar', onSelect: () => { setEditing(r); setOpen(true) } },
                      ...(!r.is_default ? [{ label: 'Definir padrão', onSelect: () => onMakeDefault(r.id!) }] : []),
                      { label: <span className="text-destructive">Excluir</span>, onSelect: () => onDelete(r.id!) },
                    ]}
                  />
                </Td>
              </Tr>
            ))}
            {rows.length===0 && (<Tr><Td colSpan={6} className="py-6 text-center text-sm text-foreground/70">Nenhum dado bancário cadastrado.</Td></Tr>)}
          </tbody>
        </Table>
      )}
      <BankAccountEditModal open={open} onClose={()=> setOpen(false)} value={editing || undefined} onSaved={()=> { setOpen(false); load() }} />
    </div>
  )
}
